import { z, type ZodRawShape } from "zod";
import { extendZodWithOpenApi } from "zod-openapi";
import { ind, jsonBody, successResponse, tagPaths, unref } from "../util.ts";
import { created_at, host_pool_uuid, storage_pool_uuid, uuid } from "./common.ts";
import * as params from "./params.ts";

extendZodWithOpenApi(z);

const HostOsType = z
    .enum(["multi", "linux", "windows"])
    .describe("OS family accepted by the host for VM placement; only matching VMs can be created on the host")
    .openapi({ ref: "HostOsType" });

const CpuInfo = z
    .object({
        nproc: z.number(),
        cpu_model: z.string(),
        cpu_model_name: z.string(),
        libvirt_cpu_model: z.string(),
        nodes: z
            .array(
                z
                    .object({ cpulist: z.string().describe("CPU set for this node").openapi({ example: "0-3,4,5" }) })
                    .passthrough(),
            )
            .describe("NUMA node information")
            .optional(),
    })
    .openapi({ ref: "CpuInfo" });

//#region Host
const AdminHostFields = {
    allocated_vcpu: z.number(),
    allocated_vmem: z.number(),
    cpu_info: CpuInfo.optional(),
    created_at: created_at.optional(),
    host_pool_id: z.number(),
    id: z.number(),
    ip: z.string(), //.ip(),
    is_accepting_workloads: z
        .boolean()
        .describe(
            "Whether this host is a valid target for new and migrated VMs. When false, existing VMs keep running and admins can still migrate VMs here explicitly.",
        ),
    is_deleted: z
        .boolean()
        .describe("Whether the host has been removed from the cluster and hidden from normal admin views."),
    is_maintenance: z
        .boolean()
        .describe(
            "Whether workloads should be evacuated from this host. When true, resident VMs are migrated away so the host can be emptied for maintenance.",
        ),
    is_offline: z
        .boolean()
        .describe(
            "Whether this host is treated as unavailable. When true, resident VMs are recreated on other hosts, so use it only when the host is definitely stopped to avoid split-brain.",
        ),
    name: z.string(),
    os: HostOsType,
    pool_cpu_model: z.string(),
    pool_name: z.string(),
    pool_uuid: unref(host_pool_uuid).describe("Host pool currently assigned to this host"),
    status: z.number(),
    storage_pool_uuid: unref(storage_pool_uuid).optional().describe("Storage pool currently associated with this host"),
    total_cpus: z.number(),
    total_mem_gb: z.number(),
    updated_at: created_at,
    usable_vcpu_amount: z.number(),
    usable_vmem_amount: z.number(),
    uuid,
    vcpu_free: z.number(),
    vmem_free: z.number(),
} as const satisfies ZodRawShape;

export const AdminHost = z.object(AdminHostFields).openapi({ ref: "AdminHost" });

export const AdminHostUpdateBody = z
    .object({
        uuid: AdminHostFields.uuid,
        is_accepting_workloads: AdminHostFields.is_accepting_workloads.optional(),
        is_deleted: AdminHostFields.is_deleted.optional(),
        is_maintenance: AdminHostFields.is_maintenance.optional(),
        is_offline: AdminHostFields.is_offline.optional(),
    })
    .refine(
        (body) =>
            body.is_accepting_workloads !== undefined ||
            body.is_deleted !== undefined ||
            body.is_maintenance !== undefined ||
            body.is_offline !== undefined,
        "At least one host flag must be provided",
    )
    .openapi({ ref: "AdminHostUpdateBody" });

export const AdminHostEditBody = z
    .object({
        uuid: AdminHostFields.uuid,
        host_pool_uuid: unref(host_pool_uuid).optional().describe("Assigns the host to a different host pool"),
        os: HostOsType.optional(),
    })
    .openapi({ ref: "AdminHostEditBody" });
//#endregion

export const AdminHostResponse = z.array(AdminHost);

export const hostPaths = tagPaths("admin_hosts")({
    "/{location}/base-operator/host/list": {
        get: {
            summary: "List hosts",
            parameters: [params.location],
            responses: {
                ...successResponse(AdminHostResponse),
            },
        },
    },

    "/{location}/base-operator/admin/host": {
        patch: {
            summary: "Update host",
            parameters: [params.location],
            requestBody: jsonBody(AdminHostEditBody),
            responses: {
                ...successResponse(AdminHost),
            },
        },
    },

    "/{location}/base-operator/admin/host_flags": {
        put: {
            summary: "Update host flags",
            description: ind`
                Updates one or more host availability flags.

                Any flag included in the request body is set to the provided value, while omitted flags are left unchanged. At least one flag must be supplied, and multiple flags can be updated in one request.

                These changes can trigger VM migration or recreation, so use this endpoint with care.`,
            parameters: [params.location],
            requestBody: jsonBody(AdminHostUpdateBody),
            responses: {
                ...successResponse(AdminHost),
            },
        },
    },
});
